import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { API_BASE_URL } from '../config';
import './AdminsManager.css';

export default function AdminsManager() {
  const { authenticatedFetch } = useAuth();

  // Change password section
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [pwStatus, setPwStatus] = useState(null);

  // Admin list section
  const [admins, setAdmins] = useState([]);
  const [listError, setListError] = useState(null);

  // Add admin section
  const [newEmail, setNewEmail] = useState('');
  const [newName, setNewName] = useState('');
  const [newAdminPassword, setNewAdminPassword] = useState('');
  const [addStatus, setAddStatus] = useState(null);

  async function loadAdmins() {
    try {
      const res = await authenticatedFetch(`${API_BASE_URL}/api/auth/admins`);
      if (!res.ok) throw new Error('Failed to load admins');
      setAdmins(await res.json());
      setListError(null);
    } catch (e) {
      setListError(e.message);
    }
  }

  useEffect(() => {
    loadAdmins();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleChangePassword(e) {
    e.preventDefault();
    setPwStatus(null);
    if (newPassword !== confirmPassword) {
      setPwStatus({ kind: 'err', text: "New passwords don't match." });
      return;
    }
    try {
      const res = await authenticatedFetch(`${API_BASE_URL}/api/auth/me/password`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ currentPassword, newPassword }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || 'Save failed');
      setPwStatus({ kind: 'ok', text: 'Password updated.' });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (e) {
      setPwStatus({ kind: 'err', text: e.message });
    }
  }

  async function handleAddAdmin(e) {
    e.preventDefault();
    setAddStatus(null);
    try {
      const res = await authenticatedFetch(`${API_BASE_URL}/api/auth/admins`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: newEmail,
          name: newName || undefined,
          password: newAdminPassword,
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || 'Add failed');
      setAddStatus({ kind: 'ok', text: `Admin ${body.email} created.` });
      setNewEmail('');
      setNewName('');
      setNewAdminPassword('');
      await loadAdmins();
    } catch (e) {
      setAddStatus({ kind: 'err', text: e.message });
    }
  }

  return (
    <div className="admins-manager">
      <h2>Admins</h2>

      <section className="setting-block">
        <h3>Change My Password</h3>
        <form onSubmit={handleChangePassword}>
          <label>
            Current password
            <input
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              required
            />
          </label>
          <label>
            New password (8+ characters)
            <input
              type="password"
              value={newPassword}
              minLength={8}
              onChange={(e) => setNewPassword(e.target.value)}
              required
            />
          </label>
          <label>
            Confirm new password
            <input
              type="password"
              value={confirmPassword}
              minLength={8}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
            />
          </label>
          <div className="actions">
            <button type="submit">Save Password</button>
            {pwStatus && <span className={`status ${pwStatus.kind}`}>{pwStatus.text}</span>}
          </div>
        </form>
      </section>

      <section className="setting-block">
        <h3>Admin Users</h3>
        {listError && <p className="status err">{listError}</p>}
        <table className="admins-table">
          <thead>
            <tr>
              <th>Email</th>
              <th>Name</th>
              <th>Created</th>
            </tr>
          </thead>
          <tbody>
            {admins.map((a) => (
              <tr key={a.id}>
                <td>{a.email}</td>
                <td>{a.name || '—'}</td>
                <td>{new Date(a.createdAt).toLocaleDateString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section className="setting-block">
        <h3>Add New Admin</h3>
        <p className="hint">
          The new admin will log in with the password you set here, and should change it on first login.
        </p>
        <form onSubmit={handleAddAdmin}>
          <label>
            Email
            <input
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              required
            />
          </label>
          <label>
            Name (optional)
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
            />
          </label>
          <label>
            Initial password (8+ characters)
            <input
              type="password"
              value={newAdminPassword}
              minLength={8}
              onChange={(e) => setNewAdminPassword(e.target.value)}
              required
            />
          </label>
          <div className="actions">
            <button type="submit">Add Admin</button>
            {addStatus && <span className={`status ${addStatus.kind}`}>{addStatus.text}</span>}
          </div>
        </form>
      </section>
    </div>
  );
}
